import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Logo } from '@/components/brand/Logo';
import { useAuth } from '@/app/AuthProvider';
import { LoadingScreen } from '@/components/layout/LoadingScreen';

export function SignInPage() {
  const { session, loading, signInWithGoogle } = useAuth();
  const location = useLocation();
  const [signingIn, setSigningIn] = useState(false);

  // Surface OAuth error params returned by Supabase via the URL hash.
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('error_description')) {
      const params = new URLSearchParams(hash.replace('#', ''));
      const desc = params.get('error_description');
      if (desc) toast.error(decodeURIComponent(desc.replace(/\+/g, ' ')));
    }
  }, []);

  if (loading) return <LoadingScreen />;
  if (session) {
    const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/dashboard';
    return <Navigate to={from} replace />;
  }

  const handleSignIn = async () => {
    setSigningIn(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      console.error(err);
      toast.error('Could not start sign-in. Please try again.');
      setSigningIn(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full bg-humus text-linen">
      {/* Left rail — brand presence using the alternate (Bright Moss + white) lockup
          per Brand Guidelines p.13: alternate is for use on Humus surfaces. */}
      <div className="hidden flex-1 flex-col justify-between p-12 md:flex">
        <Logo variant="primary" tone="alternate" className="h-9" />
        <div className="max-w-md space-y-3">
          <h1 className="text-4xl font-medium tracking-headline leading-headline">
            Atlas
          </h1>
        </div>
        <p className="text-xs text-linen/50">
          Internal use only. Sign in with your @virro account to continue.
        </p>
      </div>

      {/* Right rail — sign-in card */}
      <div className="flex w-full items-center justify-center p-6 md:w-[28rem] md:bg-card md:p-12">
        <div className="w-full max-w-sm space-y-6">
          <div className="space-y-2 md:hidden">
            <Logo variant="primary" tone="alternate" className="h-8" />
            <p className="text-sm text-linen/70">Atlas — internal ERP</p>
          </div>
          <div className="space-y-2 md:text-card-foreground">
            <h2 className="text-2xl font-medium tracking-headline leading-headline">
              Sign in
            </h2>
          </div>
          <Button
            onClick={handleSignIn}
            disabled={signingIn}
            size="lg"
            className="w-full bg-card text-foreground hover:bg-secondary md:bg-primary md:text-primary-foreground md:hover:bg-old-growth"
          >
            <GoogleGlyph className="h-5 w-5" />
            {signingIn ? 'Redirecting…' : 'Continue with Google'}
          </Button>
          <p className="text-xs text-linen/50 md:text-muted-foreground">
            By continuing you agree to the{' '}
            <Link
              to="/legal/eula"
              className="underline underline-offset-2 hover:text-linen md:hover:text-foreground"
            >
              Atlas End-User License Agreement
            </Link>{' '}
            and to follow Virro&rsquo;s internal data handling policies.
            Activity is audit-logged.
          </p>
        </div>
      </div>
    </div>
  );
}

function GoogleGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden>
      <path
        fill="#FFC107"
        d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
      />
      <path
        fill="#FF3D00"
        d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
      />
    </svg>
  );
}
